/* eslint-disable */

/**
 * Insurance Verification Vuex module — Slice 6a (foundation + dashboard)
 *
 * Wires the "Benefits & eligibility" dashboard to the insurance-verification
 * backend service. All calls are POST, base = VUE_APP_TEAMIO_API_URL (the
 * insurance-verification-service reuses the main API Gateway, paths prefixed
 * `insurance-verification/…`).
 *
 * Slice 6a endpoints:
 *   - POST insurance-verification/dashboard        { siteID, fromDateTime, toDateTime }
 *
 * Slice 6b endpoints:
 *   - POST insurance-verification/detail           { siteID, patNum, verificationId? }
 *   - POST insurance-verification/verify           { siteID, patNum, provNum?, aptNum?, aptDateTime?, triggerType }
 *   - POST insurance-verification/patient-status   { siteID, patNum }  (PatientProfile chip)
 *   - POST insurance-verification/resume-carrier   { siteID, carrierNum, employeeID }  (pause banner)
 *
 * The dashboard endpoint is feature-flag hard-gated: a 403 means
 * INSURANCE_VERIFICATION_ENABLED is off for the site. We surface that as
 * `dashboard.enabled = false` (never throw) so the page can degrade instead of
 * blowing up — the route guard normally prevents reaching the page at all.
 *
 * Axios / error idiom copied from `store/txPresenter.js` (latest-wins fetch
 * guard, response.data.success unwrap, error message into state).
 */

import axios from 'axios';

// The insurance-verification service shares the main API Gateway; base URL is
// already set globally in main.js, but mirror the other store modules and set
// it here defensively so a direct import can't race an unset default.
axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

// Latest-wins guard for the dashboard fetch. Changing the date range while an
// earlier fetch is still in flight must not let the slower response clobber (or
// stop the spinner for) the newer one. Module-scoped counter — fetchDashboard
// captures its seq and only commits if it's still the latest.
let dashboardFetchSeq = 0;

// Same guard for the patient search — its own counter, not the dashboard's. A shared one
// would make a search cancel an in-flight dashboard load (and vice versa), which is the
// bug the drawer's secondary slice already had to be given its own counter to avoid.
let patientSearchSeq = 0;

// Latest-wins guard for the detail-drawer fetch: opening row B while row A's
// detail is still loading must not let A's slower response paint into B's drawer.
let detailFetchSeq = 0;

// The secondary plan gets its OWN counter. Sharing one would make a secondary
// fetch cancel an in-flight primary fetch (and vice versa) — they are different
// slots and are routinely in flight together when a dual-plan drawer opens.
let secondaryDetailFetchSeq = 0;

// Latest-wins guard for the Credits-tab usage PANEL (the shared billed-check meter). The
// month can be switched quickly; a slower earlier month's response must not clobber the
// newer one. Its own counter — independent of the settings-modal's direct fetchUsage call.
let usagePanelFetchSeq = 0;

// Latest-wins guard for the usage LOG (the drill-down list). Trigger filter + pagination
// change fast; the stale earlier response must be dropped, never painted.
let usageLogFetchSeq = 0;

// Mutation names + seq accessors per detail slot. `fetchDetail` selects one of
// these so BOTH plans run the identical fetch body — the 404-is-not-an-error,
// soft-gate and latest-wins rules are subtle enough that a forked copy would drift.
const DETAIL_SLOTS = {
  primary: {
    LOADING: 'SET_DETAIL_LOADING',
    ERROR: 'SET_DETAIL_ERROR',
    NOTFOUND: 'SET_DETAIL_NOTFOUND',
    DATA: 'SET_DETAIL_DATA',
    next: () => ++detailFetchSeq,
    current: () => detailFetchSeq,
  },
  secondary: {
    LOADING: 'SET_SECONDARY_DETAIL_LOADING',
    ERROR: 'SET_SECONDARY_DETAIL_ERROR',
    NOTFOUND: 'SET_SECONDARY_DETAIL_NOTFOUND',
    DATA: 'SET_SECONDARY_DETAIL_DATA',
    next: () => ++secondaryDetailFetchSeq,
    current: () => secondaryDetailFetchSeq,
  },
};

// Pull the most useful message out of an axios error. Our backend's
// createErrorResponse JSON.stringify's the raw message, so a 400/500 body is
// EITHER a bare string ('Invalid settings: …') OR an object ({ message } /
// { notRenderable }). Fall back to err.message, then a caller-supplied default.
function errMessage(err, fallback) {
  const resp = err && err.response;
  if (resp && resp.data !== undefined && resp.data !== null) {
    if (typeof resp.data === 'string' && resp.data.trim() !== '') return resp.data;
    if (typeof resp.data === 'object' && resp.data.message) return resp.data.message;
  }
  return (err && err.message) || fallback;
}

// Empty dashboard shape — reused by RESET and by the 403 (feature-off) path so
// the page never renders stale rows for a site that just had the flag flipped.
function emptyDashboard() {
  return {
    rows: [],
    statCards: { verified: 0, needAttention: 0, coverageAlerts: 0 },
    chipCounts: {},
    staleCount: 0,
    unverifiedCount: 0,
    unmappedCarriers: [],
    excludedCarriers: [],
    // ⛔ Declared here, not merely assigned in SET_DASHBOARD_DATA: Vue 2 cannot make a key
    // added to an object AFTER creation reactive, so the retry notice would render against a
    // stale null on first load and only appear after some unrelated re-render.
    automation: null,
    // Carriers whose payer rejected the practice's billing provider (2026-09-18, backend
    // migration 005): automatic checks are PAUSED for them until an admin clicks Resume.
    // Declared here for the same Vue 2 reactivity reason as `automation`. [] from an older
    // backend → no banner.
    carrierPauses: [],
    // OD preflight blocks (lib/preflight.js) surfaced by dashboard-assemble:
    // distinct BY CODE with occurrence counts, [{ code, detail, count }]. The
    // per-code tallies ALSO arrive folded into chipCounts, so the Action-Required
    // chips render off that; this array drives the aggregate "fix these in Open
    // Dental" nudge. `dataIssuesCount` is the total blocked ROWS (not codes).
    dataIssues: [],
    dataIssuesCount: 0,
    fromDateTime: null,
    toDateTime: null,
    siteID: null,
  };
}

// Empty usage drill-down log shape — reused by the initial state AND by RESET_USAGE_LOG so the
// two can never drift. The hand-typed reset previously forgot `limit`, so a fetch that bumped it
// to 50 then closed left 50 behind; sourcing both from one factory guarantees a close restores
// the default 25. Mirrors the emptyDashboard()/emptyDetail() pattern above.
function emptyUsageLog() {
  return {
    rows: [],
    page: 1,
    limit: 25,
    total: 0,
    hasMore: false,
    trigger: null,
    loading: false,
    open: false,
  };
}

// Empty detail-drawer shape — reused by RESET and by the 404 (patient has no
// verification yet) path so the drawer degrades to an empty state, never stale.
function emptyDetail() {
  return {
    verification: null,   // full row, Raw271/RequestSnapshot dropped, Summary/Warnings parsed
    // The OD-chart `serviceHistory` list is GONE (2026-08-12). It duplicated Open Dental,
    // which the front desk already has open, and sat under the payer-sourced panel where the
    // two read as one contradictory list. The office chart now reaches the UI only as
    // `inOfficeChart` on each Summary.serviceHistory row — the part OD cannot tell you.
    policyHolder: null,   // { patNum, firstName, lastName, isPatient } — whose plan this is
    odLastVerified: null, // OD insverify DateLastVerified (string) or null
    patNum: null,
    verificationId: null,
    /* The per-CDT coverage table, BUILT BY THE BACKEND (lib/procedure-rows.js) so the drawer
     * and the handout PDF cannot disagree. Shape:
     *   { groups: [{ category, rows: [row] }], payerExtras: [row], hasEstimate, hasAnyCoverage }
     * null when the service predates this (2026-08-24) — the drawer then falls back to its old
     * payer-only rendering, which is why an older backend degrades instead of blanking. */
    procedureRows: null,
  };
}

export default {
  namespaced: true,

  state: {
    // Dashboard payload from /insurance-verification/dashboard, plus request
    // meta and UI state. `enabled` starts true (optimistic — the route guard
    // gates entry); a 403 flips it false and the page shows a "not enabled"
    // notice instead of an error.
    dashboard: {
      ...emptyDashboard(),
      loading: false,
      error: null,
      enabled: true,
      lastFetchedAt: null,
    },

    /**
     * Patient-search results — a SEPARATE slice, deliberately not written over
     * `dashboard`.
     *
     * The dashboard is the day's worklist and is expensive to rebuild; search is a
     * detour. Keeping them apart means "Back to schedule" is instant and cannot lose the
     * filters or the date window the user had set up. It also keeps the two straight in
     * the UI: a search result has no appointment, so anything that reasons about the
     * schedule must not silently consume search rows.
     */
    search: {
      rows: [],
      q: '',
      truncated: false,
      loading: false,
      error: null,
      // True once a search has been RUN — drives the results mode. Distinct from a
      // non-empty `q`, which is just text in the box feeding the client-side filter.
      active: false,
    },

    // Detail-drawer payload from /insurance-verification/detail (Slice 6b). One
    // patient's current (or a specific) verification + OD service history.
    // `notFound` is the 404 empty state (patient never verified) — NOT an error.
    detail: {
      ...emptyDetail(),
      loading: false,
      error: null,
      notFound: false,
      lastFetchedAt: null,
    },

    // The SECONDARY plan's (patplan.Ordinal = 2) detail, when the patient carries
    // one. Its own slice rather than a generic keyed cache on purpose: `detail`
    // keeps meaning exactly what it always meant — the primary — so no existing
    // reader changes behaviour, and the two can be in flight independently.
    // Populated only when the drawer's Secondary tab is opened; a patient with one
    // plan never fetches it.
    secondaryDetail: {
      ...emptyDetail(),
      loading: false,
      error: null,
      notFound: false,
      lastFetchedAt: null,
    },

    // Settings cache — populated by the Precheck settings modal (Slice 6c).
    // Kept here so 6a's dashboard and 6c's settings editor share one source.
    // `null` = not yet fetched; the page falls back to sensible defaults
    // (e.g. staleDays = 30) until then.
    settings: {
      siteID: null,
      value: null,          // the resolved settings blob (defaults ⊕ stored)
      featureEnabled: null, // INSURANCE_VERIFICATION_ENABLED for the site (null = unknown)
      loading: false,
      error: null,
    },

    // Credits-tab Usage report card — the billed-check meter for the current month. `data`
    // is the /usage payload (same shape fetchUsage returns); the shared `usageMeterView`
    // formatter turns `data.counts` into the rendered band/split. Null until loaded, and it
    // STAYS null on failure so a meter outage never blocks the card.
    usagePanel: {
      data: null,
      loading: false,
    },

    // The usage drill-down list (a drawer/expander off the meter). Rows are individual
    // persisted checks for the month, optionally filtered to one trigger, paginated. `open`
    // drives the drawer; `trigger` is the active filter (null = all).
    usageLog: emptyUsageLog(),
  },

  getters: {
    dashboardRows: (state) => state.dashboard.rows,
    dashboardStatCards: (state) => state.dashboard.statCards,
    dashboardChipCounts: (state) => state.dashboard.chipCounts,
    dashboardUnmappedCarriers: (state) => state.dashboard.unmappedCarriers,
    dashboardDataIssues: (state) => state.dashboard.dataIssues,
    dashboardDataIssuesCount: (state) => state.dashboard.dataIssuesCount,
    dashboardStaleCount: (state) => state.dashboard.staleCount,
    dashboardLoading: (state) => state.dashboard.loading,
    dashboardError: (state) => state.dashboard.error,
    dashboardEnabled: (state) => state.dashboard.enabled,

    detailVerification: (state) => state.detail.verification,
    detailPolicyHolder: (state) => state.detail.policyHolder,
    detailProcedureRows: (state) => state.detail.procedureRows,
    detailLoading: (state) => state.detail.loading,
    detailError: (state) => state.detail.error,
    detailNotFound: (state) => state.detail.notFound,

    secondaryDetailVerification: (state) => state.secondaryDetail.verification,
    secondaryDetailLoading: (state) => state.secondaryDetail.loading,
    secondaryDetailError: (state) => state.secondaryDetail.error,
    secondaryDetailNotFound: (state) => state.secondaryDetail.notFound,

    // Resolved settings blob (or null until first fetch). The dashboard reads
    // staleDays / flagStale off this to drive the StaleBanner (Slice 6c).
    settingsValue: (state) => state.settings.value,
    settingsFeatureEnabled: (state) => state.settings.featureEnabled,

    // Credits-tab usage panel + drill-down log.
    usagePanelData: (state) => state.usagePanel.data,
    usagePanelLoading: (state) => state.usagePanel.loading,
    usageLogRows: (state) => state.usageLog.rows,
    usageLogOpen: (state) => state.usageLog.open,
    usageLogLoading: (state) => state.usageLog.loading,
    usageLogHasMore: (state) => state.usageLog.hasMore,
    usageLogPage: (state) => state.usageLog.page,
    usageLogTotal: (state) => state.usageLog.total,
    usageLogTrigger: (state) => state.usageLog.trigger,
  },

  mutations: {
    SET_DASHBOARD_LOADING(state, val) { state.dashboard.loading = val; },
    SET_DASHBOARD_ERROR(state, val) { state.dashboard.error = val; },
    SET_DASHBOARD_ENABLED(state, val) { state.dashboard.enabled = val; },
    SET_SEARCH_LOADING(state, v) {
      state.search.loading = !!v;
    },
    SET_SEARCH_ERROR(state, v) {
      state.search.error = v || null;
    },
    SET_SEARCH_RESULTS(state, payload) {
      const d = payload || {};
      state.search.rows = Array.isArray(d.rows) ? d.rows : [];
      state.search.q = d.q || '';
      state.search.truncated = d.truncated === true;
      state.search.active = true;
      state.search.error = null;
    },
    CLEAR_SEARCH(state) {
      state.search.rows = [];
      state.search.q = '';
      state.search.truncated = false;
      state.search.active = false;
      state.search.error = null;
      state.search.loading = false;
    },

    SET_DASHBOARD_DATA(state, payload) {
      const d = payload || {};
      state.dashboard.rows = Array.isArray(d.rows) ? d.rows : [];
      state.dashboard.statCards = d.statCards || { verified: 0, needAttention: 0, coverageAlerts: 0 };
      state.dashboard.chipCounts = d.chipCounts || {};
      state.dashboard.staleCount = d.staleCount || 0;
      state.dashboard.unverifiedCount = d.unverifiedCount || 0;
      state.dashboard.unmappedCarriers = Array.isArray(d.unmappedCarriers) ? d.unmappedCarriers : [];
      state.dashboard.excludedCarriers = Array.isArray(d.excludedCarriers) ? d.excludedCarriers : [];
      // What the nightly runner will do, straight from the same response that produced the
      // rows — so a retry notice can never describe a different run than the one on screen.
      // ⛔ null when absent, never {}: the table must say NOTHING rather than assume
      // automation is on and promise a retry that is not coming.
      state.dashboard.automation = (d.automation && typeof d.automation === 'object') ? d.automation : null;
      state.dashboard.carrierPauses = Array.isArray(d.carrierPauses) ? d.carrierPauses : [];
      state.dashboard.dataIssues = Array.isArray(d.dataIssues) ? d.dataIssues : [];
      state.dashboard.dataIssuesCount = d.dataIssuesCount || 0;
      state.dashboard.fromDateTime = d.fromDateTime || null;
      state.dashboard.toDateTime = d.toDateTime || null;
      state.dashboard.siteID = d.siteID || null;
      state.dashboard.lastFetchedAt = Date.now();
    },
    RESET_DASHBOARD(state) {
      Object.assign(state.dashboard, emptyDashboard(), {
        loading: false,
        error: null,
        // preserve `enabled` — clearing on navigation shouldn't re-flag a
        // known-off site as on.
        lastFetchedAt: null,
      });
    },

    // ─── Detail drawer (Slice 6b) ──────────────────────
    SET_DETAIL_LOADING(state, val) { state.detail.loading = val; },
    SET_DETAIL_ERROR(state, val) { state.detail.error = val; },
    SET_DETAIL_NOTFOUND(state, val) { state.detail.notFound = val; },
    SET_DETAIL_DATA(state, payload) {
      const d = payload || {};
      state.detail.verification = d.verification || null;
      state.detail.policyHolder = d.policyHolder || null;
      state.detail.odLastVerified = d.odLastVerified || null;
      state.detail.patNum = d.patNum !== undefined ? d.patNum : state.detail.patNum;
      state.detail.verificationId = (d.verification && d.verification.VerificationID) || null;
      state.detail.procedureRows = d.procedureRows || null;
      state.detail.lastFetchedAt = Date.now();
    },
    RESET_DETAIL(state) {
      Object.assign(state.detail, emptyDetail(), {
        loading: false,
        error: null,
        notFound: false,
        lastFetchedAt: null,
      });
    },

    // ─── Secondary plan detail (Ordinal 2) ─────────────
    // Deliberate mirrors of the primary mutations above. `fetchDetail` picks the
    // set by name, so both slots run the SAME fetch logic — the 404 / soft-gate /
    // latest-wins handling is subtle and must never fork between the two plans.
    SET_SECONDARY_DETAIL_LOADING(state, val) { state.secondaryDetail.loading = val; },
    SET_SECONDARY_DETAIL_ERROR(state, val) { state.secondaryDetail.error = val; },
    SET_SECONDARY_DETAIL_NOTFOUND(state, val) { state.secondaryDetail.notFound = val; },
    SET_SECONDARY_DETAIL_DATA(state, payload) {
      const d = payload || {};
      state.secondaryDetail.verification = d.verification || null;
      state.secondaryDetail.policyHolder = d.policyHolder || null;
      state.secondaryDetail.odLastVerified = d.odLastVerified || null;
      state.secondaryDetail.patNum = d.patNum !== undefined ? d.patNum : state.secondaryDetail.patNum;
      state.secondaryDetail.verificationId = (d.verification && d.verification.VerificationID) || null;
      state.secondaryDetail.procedureRows = d.procedureRows || null;
      state.secondaryDetail.lastFetchedAt = Date.now();
    },
    RESET_SECONDARY_DETAIL(state) {
      Object.assign(state.secondaryDetail, emptyDetail(), {
        loading: false,
        error: null,
        notFound: false,
        lastFetchedAt: null,
      });
    },

    // ─── Settings cache (Slice 6c) ─────────────────────
    SET_SETTINGS_LOADING(state, val) { state.settings.loading = val; },
    SET_SETTINGS_ERROR(state, val) { state.settings.error = val; },
    SET_SETTINGS_DATA(state, payload) {
      const p = payload || {};
      state.settings.siteID = p.siteID || null;
      state.settings.value = p.value || null;
      // Only overwrite featureEnabled when the caller actually knows it (get-settings
      // returns it; save-settings doesn't touch the flag, so it omits it).
      if (p.featureEnabled !== undefined) state.settings.featureEnabled = !!p.featureEnabled;
    },

    // ─── Credits-tab usage panel (the shared billed-check meter) ───────────
    SET_USAGE_PANEL_LOADING(state, val) { state.usagePanel.loading = val; },
    SET_USAGE_PANEL_DATA(state, payload) { state.usagePanel.data = payload || null; },

    // ─── Usage drill-down log ──────────────────────────────────────────────
    SET_USAGE_LOG_LOADING(state, val) { state.usageLog.loading = val; },
    SET_USAGE_LOG_OPEN(state, val) { state.usageLog.open = val; },
    SET_USAGE_LOG_DATA(state, payload) {
      const d = payload || {};
      state.usageLog.rows = Array.isArray(d.rows) ? d.rows : [];
      state.usageLog.page = d.page || 1;
      state.usageLog.limit = d.limit || state.usageLog.limit;
      state.usageLog.total = d.total || 0;
      state.usageLog.hasMore = d.hasMore === true;
      // `trigger` echoes the REQUEST filter (the endpoint doesn't return it): keep the
      // caller-supplied value, defaulting to null (all triggers) when omitted.
      state.usageLog.trigger = d.trigger !== undefined ? d.trigger : state.usageLog.trigger;
    },
    RESET_USAGE_LOG(state) {
      // Restore EVERY field from the shared factory — including `limit`, which the old
      // hand-typed reset omitted (leaving a bumped 50 behind on close).
      Object.assign(state.usageLog, emptyUsageLog());
    },
  },

  actions: {
    /**
     * Load the "Benefits & eligibility" dashboard for a site + date window.
     *
     * @param {string} siteID
     * @param {string} fromDateTime  'YYYY-MM-DD HH:MM:SS'
     * @param {string} toDateTime    'YYYY-MM-DD HH:MM:SS'
     *
     * Resolves with the dashboard payload on success. On a 403 (feature flag
     * off) it does NOT throw — it flips `dashboard.enabled = false`, clears the
     * data, and returns null. Any other failure sets `dashboard.error` and
     * rethrows so the caller can react if it wants.
     */
    /**
     * Find patients by name or PatNum, regardless of appointments.
     *
     * The dashboard's search box can only filter rows the server already returned, so it
     * can never reach a patient whose appointment is finished or who has none at all —
     * which is the whole reason this endpoint exists. Rows come back dashboard-shaped, so
     * the existing table and drawer render them unchanged.
     *
     * Read-only: it spends nothing. The billed check is still behind /verify.
     */
    async searchPatients({ commit }, { siteID, q, limit } = {}) {
      const term = (q || '').trim();
      if (!siteID || term.length < 2) return null;

      const seq = ++patientSearchSeq;
      commit('SET_SEARCH_LOADING', true);
      commit('SET_SEARCH_ERROR', null);

      try {
        const response = await axios.post(
          '/insurance-verification/patient-search',
          Object.assign({ siteID, q: term }, limit ? { limit } : {}),
          { crossdomain: true }
        );

        // Latest-wins, same guard as fetchDashboard: typing fast must not let an earlier
        // reply overwrite a later one.
        if (seq !== patientSearchSeq) return null;

        if (response.data && response.data.success) {
          const data = response.data.data || {};
          if (data.enabled === false) {
            commit('CLEAR_SEARCH');
            return null;
          }
          commit('SET_SEARCH_RESULTS', data);
          return data;
        }
        throw new Error((response.data && response.data.message) || 'Search failed');
      } catch (err) {
        if (seq !== patientSearchSeq) return null;
        console.error('[insuranceVerification] searchPatients error:', err);
        commit('SET_SEARCH_ERROR', (err && err.message) || 'Could not search patients.');
        return null;
      } finally {
        if (seq === patientSearchSeq) commit('SET_SEARCH_LOADING', false);
      }
    },

    clearPatientSearch({ commit }) {
      commit('CLEAR_SEARCH');
    },

    async fetchDashboard({ commit, state }, {
      siteID, fromDateTime, toDateTime, includeCompleted,
    } = {}) {
      if (!siteID) {
        console.warn('[insuranceVerification] fetchDashboard called without siteID');
        return null;
      }
      if (!fromDateTime || !toDateTime) {
        console.warn('[insuranceVerification] fetchDashboard called without a date range');
        return null;
      }

      const seq = ++dashboardFetchSeq;
      commit('SET_DASHBOARD_LOADING', true);
      commit('SET_DASHBOARD_ERROR', null);

      try {
        const response = await axios.post(
          '/insurance-verification/dashboard',
          // Sent as a strict boolean and only when true: the backend also reads this key
          // strictly (=== true), and it decides whether the query reaches past scheduled
          // appointments. Never send a raw status list — see the handler's header.
          Object.assign(
            { siteID, fromDateTime, toDateTime },
            includeCompleted === true ? { includeCompleted: true } : {}
          ),
          { crossdomain: true }
        );

        // Latest-wins: a newer fetch superseded this one while it was in flight.
        if (seq !== dashboardFetchSeq) return null;

        if (response.data && response.data.success) {
          // Soft-gate (backend 2026-08-05): flag OFF now answers 200 { enabled:false }
          // instead of 403, because a 403 made Lumigo page Slack on ordinary behaviour.
          // MUST be checked before reading the payload — there are no rows/statCards here.
          if (response.data.data && response.data.data.enabled === false) {
            commit('SET_DASHBOARD_ENABLED', false);
            commit('SET_DASHBOARD_DATA', emptyDashboard());
            commit('SET_DASHBOARD_ERROR', null);
            return null;
          }
          commit('SET_DASHBOARD_ENABLED', true);
          commit('SET_DASHBOARD_DATA', response.data.data);
          return response.data.data;
        }
        throw new Error((response.data && response.data.message) || 'Failed to load the insurance verification dashboard');
      } catch (err) {
        if (seq !== dashboardFetchSeq) return null;

        // Feature flag off → surface as a disabled state, not an error.
        // ⚠️ KEEP this 403 branch even though the backend soft-gates now: frontend and
        // service deploy separately, so a new bundle can talk to an old service (and a
        // browser can hold a stale bundle for days). Both shapes must mean "disabled".
        const status = err && err.response && err.response.status;
        if (status === 403) {
          commit('SET_DASHBOARD_ENABLED', false);
          commit('SET_DASHBOARD_DATA', emptyDashboard());
          commit('SET_DASHBOARD_ERROR', null);
          return null;
        }

        console.error('[insuranceVerification] fetchDashboard error:', err);
        commit('SET_DASHBOARD_ERROR', (err && err.message) || 'Failed to load dashboard');
        throw err;
      } finally {
        // Only the latest fetch clears the spinner.
        if (seq === dashboardFetchSeq) commit('SET_DASHBOARD_LOADING', false);
      }
    },

    /**
     * Load the detail-drawer payload for one patient. Prefers the specific
     * `verificationId` when the row carries one (so the drawer shows the exact
     * check the row represents); otherwise the patient's current (IsLatest=1)
     * verification. A 404 (no verification for this patient) is NOT an error —
     * it flips `detail.notFound` so the drawer shows the empty "not verified yet"
     * state with a Verify Now affordance.
     *
     * @param {string} siteID
     * @param {number|string} patNum
     * @param {number|string} [verificationId]
     * @param {number|string} [planNum] the plan the caller is asking ABOUT. Sent only on
     *        the by-patNum path, where it makes the backend resolve the same
     *        (PatNum, PlanNum) key the dashboard already matched on
     *        (lib/dashboard-assemble.js `verByPatPlan`). Without it the backend falls
     *        back to any-plan-latest, which is how a patient who changed carriers gets
     *        the OLD plan's benefits rendered under the NEW carrier's name. Ignored by
     *        backends older than the plan-aware /detail, so it is safe to send always.
     * @param {'primary'|'secondary'} [slot] which state slice to fill. 'secondary'
     *        is used by the drawer's Secondary tab, which passes the id it already
     *        has from `row.secondary.verification.verificationId`. When an id is given
     *        the endpoint just resolves it, so this only chooses where the answer lands.
     * @returns the detail payload on success, or null (not-verified / superseded / error).
     */
    async fetchDetail({ commit, dispatch }, { siteID, patNum, verificationId, planNum, slot } = {}) {
      if (!siteID || (!patNum && !verificationId)) {
        console.warn('[insuranceVerification] fetchDetail needs siteID + patNum or verificationId');
        return null;
      }

      const M = DETAIL_SLOTS[slot] || DETAIL_SLOTS.primary;
      const seq = M.next();
      commit(M.LOADING, true);
      commit(M.ERROR, null);
      commit(M.NOTFOUND, false);

      const body = { siteID };
      if (patNum !== undefined && patNum !== null && patNum !== '') body.patNum = patNum;
      if (verificationId !== undefined && verificationId !== null && verificationId !== '') {
        body.verificationId = verificationId;
      }
      // Only meaningful on the by-patNum path — an explicit id already names one exact
      // row, and sending both would invite a backend to disagree with itself.
      if (!body.verificationId && planNum !== undefined && planNum !== null && planNum !== '') {
        body.planNum = planNum;
      }

      try {
        const response = await axios.post(
          '/insurance-verification/detail',
          body,
          { crossdomain: true }
        );

        // Latest-wins: a newer drawer-open superseded this fetch.
        if (seq !== M.current()) return null;

        if (response.data && response.data.success) {
          // Soft-gate: flag OFF answers 200 { enabled:false } (see fetchDashboard).
          // Reachable only from an already-loaded dashboard, i.e. a stale tab whose site
          // was disabled mid-session — say so plainly rather than rendering an empty drawer.
          if (response.data.data && response.data.data.enabled === false) {
            commit(M.DATA, Object.assign({}, emptyDetail(), { patNum }));
            commit(M.ERROR, 'Insurance Verification is not enabled for this site.');
            return null;
          }
          /* Soft-200 "never checked": the backend answers a by-patNum lookup with no row
           * as 200 `{ verification:null, notVerified:true }` rather than a 404, because a
           * never-verified patient is the ORDINARY answer — at a day-one site ~7,700 of
           * 7,801 insured patients — and Lumigo pages on every 4xx, so the designed empty
           * state was raising a prod alert per first-open (2026-09-09, Tulip Tree). Same
           * reasoning as the flag-off 403 → 200 `{enabled:false}` change of 2026-08-05.
           * ⛔ Must land in the SAME state as the 404 branch below: a 200 whose
           * `verification` is null would otherwise leave notFound false, and the drawer's
           * `v-else-if="verification"` renders nothing — a blank panel with no Verify
           * button. This branch is what lets an OLD backend's 404 and a NEW backend's
           * soft-200 look identical to the drawer. */
          const payload = response.data.data;
          if (!payload || payload.notVerified === true || !payload.verification) {
            await dispatch('markNotVerified', { slot, patNum });
            return null;
          }
          // ⛔ Spread `response.data.data` WHOLE — a new backend field (procedureRows was
          // the last one) must reach the drawer without a store edit. Pinned by
          // cypress/node-tests/insurance-verification-procedure-parity.test.js.
          commit(M.DATA, Object.assign({}, response.data.data, { patNum }));
          return response.data.data;
        }
        throw new Error((response.data && response.data.message) || 'Failed to load verification detail');
      } catch (err) {
        if (seq !== M.current()) return null;

        // 404 → no verification for this lookup. Empty state, not an error. Still
        // handled: it is what a pre-soft-200 backend returns, and what the by-id path
        // returns for an id that does not resolve.
        const status = err && err.response && err.response.status;
        if (status === 404) {
          await dispatch('markNotVerified', { slot, patNum });
          return null;
        }

        console.error('[insuranceVerification] fetchDetail error:', err);
        commit(M.ERROR, (err && err.message) || 'Failed to load verification detail');
        throw err;
      } finally {
        if (seq === M.current()) commit(M.LOADING, false);
      }
    },

    /**
     * Run a single on-demand eligibility check for one patient.
     *
     * The backend returns 200 for BOTH a completed check AND a pre-flight bailout
     * (data.ok=false + data.reason ∈ {no_billing_provider, no_billing_npi, unmapped_carrier,
     * no_active_insurance}); the caller inspects `data.reason` to decide whether
     * to show a fix-it message or treat it as a real result. This action returns
     * that `data` payload on any 200 and RE-THROWS on an HTTP failure (403 =
     * disabled; 504/network = the result is UNKNOWN — the Lambda may still persist
     * one after API Gateway's 29s cap, or the request may never have left the
     * browser) so the caller can classify it with `classifyVerifyError`.
     *
     * The two throws AFTER a response carry `err.response`, and the pre-send guard
     * carries neither `response` nor `request` — that difference is how
     * classifyVerifyError tells "sent and refused" / "never sent" / "unknown" apart.
     *
     * Pass the appointment's provNum/aptNum/aptDateTime from the dashboard row
     * when present so the check can fall back to the appointment's provider and
     * stamp the row against the appointment.
     *
     * 💵 `ordinal` IS A COST CONTROL — read before changing it.
     *   - OMITTED  → the backend checks EVERY plan the patient carries. On a
     *                dual-plan patient that is TWO billed checks from one click.
     *                This is the default for the header "Re-check" and the table's
     *                Verify, and is why the confirm panel warns about the cost.
     *   - 1 or 2   → the backend checks ONLY that slot: one check, one charge.
     *                Used by the drawer's per-plan re-check so refreshing one
     *                stale plan does not re-bill the other.
     * The backend throws on anything other than 1 or 2, so a bad value can never
     * silently bill a check against the wrong plan — don't "helpfully" coerce here.
     */
    async verifyPatient(_ctx, {
      siteID, patNum, provNum, aptNum, aptDateTime, triggerType, ordinal,
    } = {}) {
      if (!siteID || !patNum) {
        throw new Error('verifyPatient requires siteID and patNum');
      }
      const body = { siteID, patNum, triggerType: triggerType || 'on_demand' };
      if (ordinal !== undefined && ordinal !== null && ordinal !== '') body.ordinal = ordinal;
      if (provNum !== undefined && provNum !== null && provNum !== '') body.provNum = provNum;
      if (aptNum !== undefined && aptNum !== null && aptNum !== '') body.aptNum = aptNum;
      if (aptDateTime !== undefined && aptDateTime !== null && aptDateTime !== '') body.aptDateTime = aptDateTime;

      const response = await axios.post(
        '/insurance-verification/verify',
        body,
        { crossdomain: true }
      );
      if (response.data && response.data.success) {
        // Soft-gate: flag OFF answers 200 { enabled:false } (see fetchDashboard). THROW
        // rather than return it — every caller reads this payload as a check RESULT
        // (`data.ok`, `data.reason`, `data.status`), and `{enabled:false}` has none of
        // those, so returning it would silently render "no result" as though a check had
        // run. Nothing was sent and nothing was billed; say exactly that.
        if (response.data.data && response.data.data.enabled === false) {
          const disabledErr = new Error('Insurance Verification is not enabled for this site — no check was run.');
          disabledErr.response = response;
          throw disabledErr;
        }
        return response.data.data;
      }
      const failedErr = new Error((response.data && response.data.message) || 'Verification failed');
      failedErr.response = response;
      throw failedErr;
    },

    // Clears BOTH plans. The drawer is the only consumer and it always closes on
    // both at once; leaving the secondary behind would let one patient's second
    // plan paint into the next patient's drawer.
    resetDetail({ commit }) {
      commit('RESET_DETAIL');
      commit('RESET_SECONDARY_DETAIL');
    },

    /**
     * Billed-check meter for one SITE-LOCAL month.
     *
     * ⛔ Returns a BAND, not a single "checks used" number, and consumers must render it
     * that way. `Billable` is three states (yes / unknown / no), so the honest claim is
     * `atLeast ≤ what Stedi billed us ≤ atMost`. Flattening it to one figure either
     * over-states a client's usage — the error they audit — or throws away the evidence
     * that would let the uncertain classes be proven free later.
     *
     * Ungated on the server: it reads our own counters, so a not-yet-enabled site simply
     * answers zeros. Never throws for a UI panel — resolves to null on failure so a meter
     * outage cannot block the settings modal.
     */
    async fetchUsage(_ctx, { siteID, month } = {}) {
      if (!siteID) {
        console.warn('[insuranceVerification] fetchUsage requires siteID');
        return null;
      }
      const body = { siteID };
      if (month) body.month = month;
      try {
        const response = await axios.post('/insurance-verification/usage', body, { crossdomain: true });
        if (response.data && response.data.success) return response.data.data;
        return null;
      } catch (err) {
        console.error('[insuranceVerification] fetchUsage error:', err);
        return null;
      }
    },

    /**
     * Credits-tab flavour of the billed-check meter. WRAPS the existing `fetchUsage` action
     * (never re-issuing the axios call) and commits the result into `usagePanel` so the card
     * has its own slot independent of the settings modal's local `usage`.
     *
     * A module-level seq guard drops a stale earlier response: switching the month quickly
     * must not let the slower first month clobber (or clear the spinner for) the newer one —
     * the same latest-wins rule the dashboard uses. Never throws — `fetchUsage` resolves null
     * on failure, and any surprise still lands as `{ data: null, loading: false }`.
     */
    async fetchUsageForPanel({ commit, dispatch }, { siteID, month } = {}) {
      if (!siteID) {
        console.warn('[insuranceVerification] fetchUsageForPanel requires siteID');
        return null;
      }
      const seq = ++usagePanelFetchSeq;
      commit('SET_USAGE_PANEL_LOADING', true);
      try {
        const data = await dispatch('fetchUsage', { siteID, month });
        if (seq !== usagePanelFetchSeq) return null; // superseded by a newer month
        commit('SET_USAGE_PANEL_DATA', data || null);
        return data || null;
      } catch (err) {
        if (seq !== usagePanelFetchSeq) return null;
        console.error('[insuranceVerification] fetchUsageForPanel error:', err);
        commit('SET_USAGE_PANEL_DATA', null);
        return null;
      } finally {
        // Only the latest fetch owns the spinner.
        if (seq === usagePanelFetchSeq) commit('SET_USAGE_PANEL_LOADING', false);
      }
    },

    /**
     * The usage drill-down list: individual persisted checks for a month, optionally filtered
     * to one trigger, paginated. POSTs /insurance-verification/usage-log (same axios idiom as
     * fetchUsage). Opening the log sets `open` so the drawer shows immediately; a per-action
     * seq guard drops a stale earlier response when the filter/page is changed quickly.
     *
     * Never throws — a non-success envelope OR an HTTP failure both resolve to the empty state
     * (rows cleared, hasMore false) so the drawer degrades to "nothing to show" rather than
     * breaking the Credits tab.
     *
     * @returns the `data` payload on success, or null (superseded / empty / error).
     */
    async fetchUsageLog({ commit }, {
      siteID, month, trigger, page = 1, limit = 25,
    } = {}) {
      if (!siteID) {
        console.warn('[insuranceVerification] fetchUsageLog requires siteID');
        return null;
      }
      const seq = ++usageLogFetchSeq;
      commit('SET_USAGE_LOG_OPEN', true);
      commit('SET_USAGE_LOG_LOADING', true);

      const body = { siteID, page, limit };
      if (month) body.month = month;
      if (trigger) body.trigger = trigger;

      // The endpoint does NOT echo the trigger filter, so we carry the request value through.
      const emptyPayload = {
        rows: [], page, limit, total: 0, hasMore: false, trigger: trigger || null,
      };

      try {
        const response = await axios.post(
          '/insurance-verification/usage-log',
          body,
          { crossdomain: true }
        );
        if (seq !== usageLogFetchSeq) return null; // superseded

        if (response.data && response.data.success) {
          const d = response.data.data || {};
          commit('SET_USAGE_LOG_DATA', {
            rows: d.rows,
            page: d.page || page,
            limit: d.limit || limit,
            total: d.total || 0,
            hasMore: d.hasMore === true,
            trigger: trigger || null,
          });
          return d;
        }
        // Success:false envelope → empty state, not an error.
        commit('SET_USAGE_LOG_DATA', emptyPayload);
        return null;
      } catch (err) {
        if (seq !== usageLogFetchSeq) return null;
        console.error('[insuranceVerification] fetchUsageLog error:', err);
        commit('SET_USAGE_LOG_DATA', emptyPayload);
        return null;
      } finally {
        if (seq === usageLogFetchSeq) commit('SET_USAGE_LOG_LOADING', false);
      }
    },

    // Close the drill-down and drop its rows so a re-open never flashes the previous
    // month/trigger's list before the fresh fetch lands. Bumping the seq INVALIDATES any fetch still
    // in flight — its late response fails the `seq !== usageLogFetchSeq` check and is dropped, so it
    // can't repopulate a log the user just closed.
    closeUsageLog({ commit }) {
      usageLogFetchSeq += 1;
      commit('RESET_USAGE_LOG');
    },

    /**
     * Put ONE detail slot into the "this plan has never been checked" empty state —
     * the same state a 404 (or the backend's soft-200 `notVerified`) produces, so the
     * drawer shows "Not verified yet" with a Verify affordance rather than a blank panel.
     *
     * ⛔ All four commits are required, and NOTFOUND must be true: the drawer's
     * `terminalRefusal` branch (VerificationDrawer.vue) reads `notFound` to tell a
     * refused check apart from a failed one. Setting DATA alone leaves the previous
     * plan's benefits on screen behind a spinner that never clears.
     *
     * ONE implementation for every caller — the 404 branch, the soft-200 branch and the
     * never-checked secondary — because "not verified" has to look identical however we
     * learned it. The rules here (404-is-not-an-error, notFound gates the drawer) are
     * subtle enough that a forked copy would drift.
     */
    markNotVerified({ commit }, { slot, patNum } = {}) {
      const M = DETAIL_SLOTS[slot] || DETAIL_SLOTS.primary;
      commit(M.DATA, Object.assign({}, emptyDetail(), { patNum }));
      commit(M.NOTFOUND, true);
      commit(M.ERROR, null);
      commit(M.LOADING, false);
    },

    /**
     * The patient carries a second plan that has never been checked, so there is no
     * verification id to fetch. Thin alias of `markNotVerified` for the secondary slot,
     * kept because the page and its tests dispatch it by name.
     *
     * We must NOT fall back to fetching by patNum here: /detail keyed by patNum alone
     * resolves the patient's latest verification, which is the PRIMARY's — i.e. the
     * primary's benefits rendered under a tab labelled "Secondary". A blank panel is
     * a bug; the wrong plan's coverage is a misquote.
     */
    markSecondaryNotVerified({ dispatch }, { patNum } = {}) {
      return dispatch('markNotVerified', { slot: 'secondary', patNum });
    },

    /**
     * Slice 6b — per-patient verification status for the PatientProfile chip.
     * Soft-gated backend (flag off → 200 `{ enabled:false }`), so this never
     * needs 403 handling. Renders NOTHING when disabled.
     *
     * @param {string} siteID
     * @param {number|string} patNum
     * @returns the `data` payload, or null on failure (non-fatal by design).
     */
    async fetchPatientStatus(_ctx, { siteID, patNum } = {}) {
      if (!siteID || !patNum) return null;
      try {
        const response = await axios.post(
          '/insurance-verification/patient-status',
          { siteID, patNum },
          { crossdomain: true }
        );
        if (response.data && response.data.success) {
          return response.data.data;
        }
        return null;
      } catch (err) {
        // Non-fatal: the profile chip must never break the page.
        console.error('[insuranceVerification] fetchPatientStatus error:', err);
        return null;
      }
    },

    resetDashboard({ commit }) {
      commit('RESET_DASHBOARD');
    },

    // ─── Settings + payer-mapping + PDF (Slice 6c) ──────────────────────────

    /**
     * Load the resolved settings blob (+ featureEnabled) for a site and cache it.
     * Cached per-site: a second call for the same site returns the cache unless
     * `force` is set (the settings modal forces a refetch on open so an edit made
     * in another tab isn't overwritten by a stale cache).
     *
     * @returns { settings, featureEnabled } (or the cached shape), null on no siteID.
     */
    async fetchSettings({ commit, state }, { siteID, force = false } = {}) {
      if (!siteID) {
        console.warn('[insuranceVerification] fetchSettings called without siteID');
        return null;
      }
      if (!force && state.settings.siteID === siteID && state.settings.value) {
        return { settings: state.settings.value, featureEnabled: state.settings.featureEnabled };
      }

      commit('SET_SETTINGS_LOADING', true);
      commit('SET_SETTINGS_ERROR', null);
      try {
        const response = await axios.post(
          '/insurance-verification/get-settings',
          { siteID },
          { crossdomain: true }
        );
        if (response.data && response.data.success) {
          const data = response.data.data || {};
          commit('SET_SETTINGS_DATA', {
            siteID,
            value: data.settings || null,
            featureEnabled: !!data.featureEnabled,
          });
          return data;
        }
        throw new Error((response.data && response.data.message) || 'Failed to load settings');
      } catch (err) {
        console.error('[insuranceVerification] fetchSettings error:', err);
        commit('SET_SETTINGS_ERROR', errMessage(err, 'Failed to load settings'));
        throw err;
      } finally {
        commit('SET_SETTINGS_LOADING', false);
      }
    },

    /**
     * "Resume checks" on a carrier pause (2026-09-18, backend migration 005). A pause opens when
     * a payer rejects the practice's BILLING PROVIDER; this is the ONLY thing that lifts it.
     *
     * ⛔ It RESTARTS SPEND on that carrier — the page shows the button to admins only (the
     * Precheck-settings convention). The backend is idempotent: resuming a carrier that is no
     * longer paused answers { resumed:false }, not an error. Soft-gate { enabled:false } THROWS,
     * like verifyPatient — the caller reads the payload as a result, and "nothing happened" must
     * not look like success. The caller refetches the dashboard to drop the banner.
     */
    async resumeCarrier(_ctx, { siteID, carrierNum, employeeID } = {}) {
      if (!siteID || carrierNum === undefined || carrierNum === null || carrierNum === '') {
        throw new Error('resumeCarrier requires siteID and carrierNum');
      }
      const body = { siteID, carrierNum };
      if (employeeID !== undefined && employeeID !== null && employeeID !== '') body.employeeID = employeeID;
      const response = await axios.post('/insurance-verification/resume-carrier', body, { crossdomain: true });
      if (response.data && response.data.success) {
        if (response.data.data && response.data.data.enabled === false) {
          throw new Error('Insurance Verification is not enabled for this site — nothing was resumed.');
        }
        return response.data.data;
      }
      throw new Error((response.data && response.data.message) || 'Could not resume checks for this carrier.');
    },

    /**
     * Persist the WHOLE settings blob (the backend merges defaults ⊕ stored ⊕
     * patch and write-verifies). On success the returned merged blob replaces the
     * cache so the dashboard's staleDays/flagStale react immediately. Re-throws on
     * failure with a friendly message (validation errors arrive as a 400 string).
     */
    async saveSettings({ commit, state }, { siteID, settings } = {}) {
      if (!siteID || !settings || typeof settings !== 'object') {
        throw new Error('saveSettings requires siteID and a settings object');
      }
      commit('SET_SETTINGS_LOADING', true);
      commit('SET_SETTINGS_ERROR', null);
      try {
        const response = await axios.post(
          '/insurance-verification/save-settings',
          { siteID, settings },
          { crossdomain: true }
        );
        if (response.data && response.data.success) {
          const data = response.data.data || {};
          commit('SET_SETTINGS_DATA', {
            siteID,
            value: data.settings || settings,
            // save-settings never changes the feature flag — preserve the cached one.
            featureEnabled: state.settings.featureEnabled,
          });
          return data;
        }
        throw new Error((response.data && response.data.message) || 'Failed to save settings');
      } catch (err) {
        console.error('[insuranceVerification] saveSettings error:', err);
        const msg = errMessage(err, 'Failed to save settings');
        commit('SET_SETTINGS_ERROR', msg);
        throw new Error(msg);
      } finally {
        commit('SET_SETTINGS_LOADING', false);
      }
    },

    /**
     * OD imaging categories (definition Category=18) for the "import PDF to OD"
     * folder select. Not cached in the store (the modal holds the list for its
     * open lifetime). Returns [] on no siteID.
     */
    async fetchImagingCategories(_ctx, { siteID } = {}) {
      if (!siteID) return [];
      const response = await axios.post(
        '/insurance-verification/get-imaging-categories',
        { siteID },
        { crossdomain: true }
      );
      if (response.data && response.data.success) {
        return (response.data.data && response.data.data.categories) || [];
      }
      throw new Error((response.data && response.data.message) || 'Failed to load imaging categories');
    },

    /**
     * OD Appointment Field Defs (apptfielddef) for the "Change value of Appointment Field
     * Defs" field select. Same contract as fetchImagingCategories: not cached, [] on no
     * siteID, throws on a non-success body. Returns [{ apptFieldDefNum, fieldName }] — the
     * write keys on `fieldName`, the DefNum is only a list key.
     */
    async fetchApptFieldDefs(_ctx, { siteID } = {}) {
      if (!siteID) return [];
      const response = await axios.post(
        '/insurance-verification/get-appt-field-defs',
        { siteID },
        { crossdomain: true }
      );
      if (response.data && response.data.success) {
        return (response.data.data && response.data.data.fields) || [];
      }
      throw new Error((response.data && response.data.message) || 'Failed to load appointment fields');
    },

    /**
     * The payer-mapping screen data: this site's mappings (own overrides + global
     * defaults) plus all visible carriers with their ElectID (for mapped-vs-unmapped
     * state). Returns { mappings, carriers }.
     */
    async fetchPayerMapping(_ctx, { siteID } = {}) {
      if (!siteID) return { mappings: [], carriers: [] };
      const response = await axios.post(
        '/insurance-verification/get-payer-mapping',
        { siteID },
        { crossdomain: true }
      );
      if (response.data && response.data.success) {
        return response.data.data || { mappings: [], carriers: [] };
      }
      throw new Error((response.data && response.data.message) || 'Failed to load payer mapping');
    },

    /**
     * Upsert a per-site carrier→payer override. Body matches the handler exactly
     * ({ siteID, carrierNum, carrierName, payerId, payerName?, lineOfBusiness? }).
     * Returns the persisted { mapping } row (incl. its PayerMapID).
     */
    async savePayerMapping(_ctx, {
      siteID, carrierNum, carrierName, payerId, payerName, lineOfBusiness,
    } = {}) {
      if (!siteID || carrierNum === null || carrierNum === undefined || carrierNum === '') {
        throw new Error('savePayerMapping requires siteID and carrierNum');
      }
      if (!carrierName) throw new Error('savePayerMapping requires carrierName');
      if (payerId === null || payerId === undefined || String(payerId).trim() === '') {
        throw new Error('A payer ID is required');
      }
      const body = { siteID, carrierNum, carrierName, payerId: String(payerId).trim() };
      if (payerName !== undefined && payerName !== null && payerName !== '') body.payerName = payerName;
      if (lineOfBusiness) body.lineOfBusiness = lineOfBusiness;

      try {
        const response = await axios.post(
          '/insurance-verification/save-payer-mapping',
          body,
          { crossdomain: true }
        );
        if (response.data && response.data.success) {
          return response.data.data;
        }
        throw new Error((response.data && response.data.message) || 'Failed to save payer mapping');
      } catch (err) {
        console.error('[insuranceVerification] savePayerMapping error:', err);
        throw new Error(errMessage(err, 'Failed to save payer mapping'));
      }
    },

    /**
     * Fetch a presigned PDF URL for a verified row's benefits handout (the drawer's
     * Download / Print buttons). NOTE: get-pdf-url returns the payload UNWRAPPED —
     * `response.data` is `{ url, pdfStatus }` directly (no { success, data } envelope).
     * A 409 { notRenderable } (non-verified row) or 403 (feature off / cross-site)
     * rejects; the caller inspects err.response.status / err.response.data.
     */
    async fetchPdfUrl(_ctx, { siteID, verificationID } = {}) {
      if (!siteID || verificationID === null || verificationID === undefined || verificationID === '') {
        throw new Error('fetchPdfUrl requires siteID and verificationID');
      }
      const response = await axios.post(
        '/insurance-verification/get-pdf-url',
        { siteID, verificationID },
        { crossdomain: true }
      );
      // Soft-gate: flag OFF answers 200 { enabled:false } (see fetchDashboard). Throw —
      // the caller expects a presigned `url` and would otherwise open/print nothing.
      const body = response.data && response.data.data;
      if (body && body.enabled === false) {
        throw new Error('Insurance Verification is not enabled for this site.');
      }
      return response.data || null;
    },
  },
};
